import React from "react";
import { Box, IconButton, Tooltip } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { ReactComponent as ThumbsUp } from "../../assets/icons/ThumbsUp-Dark.svg";
import { ReactComponent as ThumbsDown } from "../../assets/icons/ThumbsDown-Dark.svg";
import { ReactComponent as CopyIcon } from "../../assets/icons/Copy-Dark.svg";
import {
  ChatMode,
  ChatModeValues,
  FeedbackReaction,
} from "../../types/frontend";

/**
 * MessageActionsProps interface defines the props for the MessageActions component.
 */
interface MessageActionsProps {
  /** Whether the message is from the assistant. */
  isAssistantMessage: boolean;
  /** Whether a response is currently being generated. */
  isLoading?: boolean;
  /** Whether this message is the last message in the chat. */
  isLastMessage: boolean;
  /** The selected reaction (up, down, or null). */
  selectedReaction: FeedbackReaction | null;
  /** Handler for when the copy button is clicked. */
  onCopy: () => void;
  /** Handler for when a thumb button is clicked. */
  onThumbClick: (
    event: React.MouseEvent<HTMLElement>,
    reaction: FeedbackReaction
  ) => void;
  /** The chat mode (workflow or agent). Feedback buttons are hidden in agent mode. */
  chatMode?: ChatMode;
}

/**
 * MessageActions Component
 *
 * Displays action buttons for chat messages, including copy text and feedback buttons.
 * Buttons are only shown for assistant messages and are hidden during streaming for the last message.
 *
 * @param {MessageActionsProps} props - Component props
 * @returns {JSX.Element | null} Rendered MessageActions component or null if buttons should be hidden
 */
const MessageActions: React.FC<MessageActionsProps> = ({
  isAssistantMessage,
  isLoading,
  isLastMessage,
  selectedReaction,
  onCopy,
  onThumbClick,
  chatMode = ChatModeValues.Workflow,
}) => {
  const theme = useTheme();

  if (!isAssistantMessage || (isLoading && isLastMessage)) {
    return null;
  }

  const activeColor = theme.palette.primary.light;
  const inactiveColor = theme.palette.grey[500];
  const commonIconButtonSx = {
    bgcolor: "grey.100",
    "&:hover": {
      bgcolor: "grey.300",
    },
    "& svg": {
      color: "inherit",
      fill: "currentColor",
      stroke: "currentColor",
    },
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "row", gap: 1, mt: 1 }}>
      {/* Copy text */}
      <Tooltip title="Copy text">
        <IconButton
          disableRipple
          onClick={onCopy}
          data-attr="copy-model-response"
          sx={{
            ...commonIconButtonSx,
            color: inactiveColor,
            "&:hover": {
              ...commonIconButtonSx["&:hover"],
              color: activeColor,
            },
          }}
        >
          <CopyIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      {/* Feedback thumbs up - hidden in agent mode */}
      {chatMode !== ChatModeValues.Agent && (
        <Tooltip title="Was this helpful?">
          <IconButton
            disableRipple
            onClick={(e) => onThumbClick(e, FeedbackReaction.Up)}
            data-attr="feedback-thumbs-up"
            sx={{
              ...commonIconButtonSx,
              color: inactiveColor,
              "&:hover": {
                ...commonIconButtonSx["&:hover"],
                color: activeColor,
              },
            }}
          >
            <ThumbsUp fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
      {/* Feedback thumbs down - hidden in agent mode */}
      {chatMode !== ChatModeValues.Agent && (
        <Tooltip title="Was this not helpful?">
          <IconButton
            disableRipple
            onClick={(e) => onThumbClick(e, FeedbackReaction.Down)}
            data-attr="feedback-thumbs-down"
            sx={{
              ...commonIconButtonSx,
              color: inactiveColor,
              "&:hover": {
                ...commonIconButtonSx["&:hover"],
                color: activeColor,
              },
            }}
          >
            <ThumbsDown fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
};

export default MessageActions;
